/**
 * People on a phone and an iPad, in the Season design: a search, "Who to show" and a switch between Faces and List.
 * A phone opens the choices in a sheet and docks the group's note; an iPad upright keeps them in a pane at the right;
 * on its side the list sits beside the person that is open, like Mail. It reads what the computer's People reads.
 */
import { useState } from "react";
import { Link } from "react-router";
import type { RegistrantSummary } from "../../../shared/types";
import { FirstRoomEmpty } from "../../components/FirstRoomEmpty";
import { DockedBar } from "../../components/season/DockedBar";
import { Empty } from "../../components/season/Empty";
import { SeasonCard } from "../../components/season/Card";
import { GroupedList, ListRow } from "../../components/season/GroupedList";
import type { SeasonLayout } from "../../components/season/layout";
import { Page } from "../../components/season/Page";
import { PageHead } from "../../components/season/PageHead";
import { Pane, Panes } from "../../components/season/Panes";
import { Search } from "../../components/season/Search";
import { Switch } from "../../components/season/Switch";
import { Button } from "../../components/ui/Button";
import { FaceStack, Ring } from "../../components/ui/Face";
import { Icon } from "../../components/ui/Icon";
import { Menu } from "../../components/ui/Menu";
import { Sheet } from "../../components/ui/Sheet";
import { fmt } from "../../lib/when";
import { depthOf, lastLine, peopleWord, usePeople } from "./list";
import { SeasonPerson } from "./SeasonPerson";
import { WhoToShow } from "./seasonSheets";
import { AutomationSheet, GroupNoteSheet } from "./sheets";
import { StagePill, stageLabel } from "./stage";
import "../../styles/season-people.css";

const VIEWS = [
  { value: "faces", label: "Faces", icon: "grid" },
  { value: "list", label: "List", icon: "list" },
] as const;

/** How far in they are, as the second line under a face: the minute while they watch, else the share watched. */
function depthLine(p: RegistrantSummary): string {
  if (p.watching) return `${p.watching.minute} of ${p.watching.of_minutes} min`;
  return p.pressed_play ? `Watched ${p.watched_pct}%` : "Hasn't pressed play";
}

export function SeasonPeople({ layout, openId }: { layout: SeasonLayout; openId?: string }) {
  const P = usePeople();
  const [picking, setPicking] = useState(false);
  const [groupNote, setGroupNote] = useState(false);
  const [autoFor, setAutoFor] = useState<string[] | null>(null);
  const side = layout === "side";
  const { data, rail, filters, shown, n, groupAct } = P;
  const open = openId ?? shown[0]?.id;

  const head = (right?: React.ReactNode) => <PageHead title="People" count={rail ? fmt(P.everyone) : undefined} right={right} />;
  const menu = (
    <Menu
      label="More for People"
      buttonClassName="k-ib"
      items={[
        { label: "Add to automation", icon: "zap", onSelect: () => setAutoFor(shown.map((p) => p.id)), disabled: n === 0 },
        { label: "Export", icon: "download", onSelect: P.exportCsv, disabled: n === 0 },
      ]}
    />
  );
  const sheets = (
    <>
      <Sheet open={picking} onClose={() => setPicking(false)} title="Who to show">
        {rail ? <WhoToShow rail={rail} filters={filters} onPick={() => setPicking(false)} /> : null}
      </Sheet>
      {groupAct ? (
        <GroupNoteSheet open={groupNote} title={groupAct.label} label={P.title} about={groupAct.about} people={shown} onClose={() => setGroupNote(false)} />
      ) : null}
      <AutomationSheet open={autoFor !== null} ids={autoFor ?? []} onClose={() => setAutoFor(null)} />
    </>
  );

  if (rail && P.everyone === 0) {
    return (
      <Page layout={layout} narrow className="pp">
        {head()}
        <SeasonCard>
          <FirstRoomEmpty place="people" />
        </SeasonCard>
      </Page>
    );
  }

  const filterButton = (
    <button type="button" className="pp-filter" aria-label={`Who to show: ${P.title}`} onClick={() => setPicking(true)}>
      <span className="pp-ft">
        <em>Who to show</em>
        <b>{P.title}</b>
      </span>
      <span className="pp-fn">{fmt(P.total)}</span>
      <Icon name="down" />
    </button>
  );
  const groupButton =
    groupAct && n > 0 ? (
      <Button className="pp-act" icon="send" onClick={() => setGroupNote(true)}>
        {groupAct.label}
      </Button>
    ) : null;
  const search = <Search placeholder="Find someone" label="Find someone" onSearch={P.setQ} />;
  const switcher = <Switch options={VIEWS} value={P.view} onChange={P.setView} label="Show people as" />;
  const body = (
    <Body
      layout={layout}
      people={shown.slice(0, P.limit)}
      loaded={!!data}
      failed={P.failed && !data}
      searching={!!P.q}
      more={n - P.limit}
      next={shown.slice(P.limit, P.limit + 4)}
      view={P.view}
      open={open}
      search={P.search}
      onMore={P.showMore}
    />
  );

  if (side) {
    return (
      <>
        <Panes className="pp-split">
          <Pane kind="list" keep="people-list" label="People">
            <div className="k-sp-lh">
              <div className="k-head pp-lh">
                <h1 className="k-title">
                  People {rail ? <small>{fmt(P.everyone)}</small> : null}
                </h1>
                {menu}
              </div>
            </div>
            <Page layout="side" className="pp">
              {search}
              {filterButton}
              {groupButton}
              {body}
            </Page>
          </Pane>
          <Pane kind="main" showing={open ?? null} label="The person that is open">
            {open ? <SeasonPerson key={open} id={open} layout="side" embedded /> : <Page layout="side" className="pp"><Empty title="No one here yet" /></Page>}
          </Pane>
        </Panes>
        {sheets}
      </>
    );
  }

  if (layout === "upright") {
    return (
      <Page layout={layout} narrow className="pp">
        {head(
          <Button icon="download" onClick={P.exportCsv} disabled={n === 0}>
            Export
          </Button>,
        )}
        {search}
        {switcher}
        <div className="k-two pp-two">
          <div>
            <SeasonCard className="pp-seg">
              <div className="pp-segh">
                <h2>{P.title}</h2>
                <span className="pill">{`${fmt(n)} ${peopleWord(n)}`}</span>
                {P.clearable ? (
                  <Link className="k-link" to="/people">
                    <Icon name="x" /> Clear
                  </Link>
                ) : null}
              </div>
              <div className="pp-segb">
                {groupButton}
                <Button icon="zap" onClick={() => setAutoFor(shown.map((p) => p.id))} disabled={n === 0}>
                  Add to automation
                </Button>
              </div>
            </SeasonCard>
            <div className="pp-bodyu">{body}</div>
          </div>
          <aside className="k-pane pp-pane" aria-label="Who to show">
            {rail ? <WhoToShow rail={rail} filters={filters} /> : null}
          </aside>
        </div>
        {sheets}
      </Page>
    );
  }

  return (
    <Page layout={layout} className="pp">
      {head(menu)}
      {search}
      {filterButton}
      {switcher}
      {body}
      {groupAct && n > 0 ? (
        <DockedBar>
          <Button variant="p" full icon="send" onClick={() => setGroupNote(true)}>
            {groupAct.label}
          </Button>
        </DockedBar>
      ) : null}
      {sheets}
    </Page>
  );
}

// --- the faces and the rows ---

function Body({
  layout,
  people,
  loaded,
  failed,
  searching,
  more,
  next,
  view,
  open,
  search,
  onMore,
}: {
  layout: SeasonLayout;
  people: RegistrantSummary[];
  loaded: boolean;
  failed: boolean;
  searching: boolean;
  more: number;
  next: RegistrantSummary[];
  view: "faces" | "list";
  open: string | undefined;
  /** The address's query, so a person opened from a list keeps the list's group. */
  search: string;
  onMore: () => void;
}) {
  if (failed) {
    return (
      <SeasonCard className="pp-state">
        <span>People didn't load.</span>
        <Button onClick={() => window.location.reload()}>Try again</Button>
      </SeasonCard>
    );
  }
  if (!loaded) {
    return (
      <p className="k-sub" aria-busy="true">
        Loading
      </p>
    );
  }
  if (people.length === 0) return <Empty title={searching ? "No one by that name here" : "No one here yet"} />;
  const side = layout === "side";
  return (
    <>
      {side || view === "list" ? (
        <GroupedList className="pp-rows">
          {people.map((p) => (
            <ListRow
              key={p.id}
              lead={<Ring name={p.name} photo={p.avatar_url} size={40} percent={depthOf(p)} live={!!p.watching} brass />}
              title={p.name}
              sub={p.watching ? depthLine(p) : `${stageLabel(p)} · ${lastLine(p)}`}
              value={p.watching ? "Live" : p.watched_pct > 0 ? `${p.watched_pct}%` : undefined}
              noChevron
              on={side && p.id === open}
              to={`/people/${p.id}${side ? search : ""}`}
              innerProps={{ "data-opens": `person:${p.id}` }}
            />
          ))}
        </GroupedList>
      ) : (
        <div className="pp-grid">
          {people.map((p) => (
            <Link key={p.id} className="pp-card" to={`/people/${p.id}`} data-opens={`person:${p.id}`}>
              <Ring name={p.name} photo={p.avatar_url} size={60} percent={depthOf(p)} live={!!p.watching} brass />
              <b><bdi>{p.name}</bdi></b>
              <StagePill p={p} />
              <span>{depthLine(p)}</span>
              <small>{lastLine(p)}</small>
            </Link>
          ))}
        </div>
      )}
      {more > 0 ? (
        <div className="pp-more">
          {layout === "upright" ? <FaceStack people={next.map((p) => ({ name: p.name, photo: p.avatar_url }))} size={36} /> : null}
          <span>
            <b>{`and ${fmt(more)} more`}</b>
          </span>
          <Button onClick={onMore}>Show more</Button>
        </div>
      ) : null}
    </>
  );
}
